import * as React from 'react';
import { createNotePageUpdates, noteQueryKey } from '../helpers/notePageUpdates';
import type { NotePageSnapshot } from '../helpers/notePageUpdates';
import type { NoteUpdatesController } from '../helpers/noteUpdates';
import type { PDFNoteQuery } from '../models/PDFNoteQuery';
import type { PDFNoteRefreshPageRequest, PDFNoteRefreshPageResponse } from '../models/PDFNoteRefreshPage';

interface UseNotePageUpdatesOptions {
  noteUpdates?: NoteUpdatesController;
  query: PDFNoteQuery;
  enabled?: boolean;
  request(request: PDFNoteRefreshPageRequest): Promise<PDFNoteRefreshPageResponse>;
  onPage(page: NotePageSnapshot, query: PDFNoteQuery): void;
  onError(error: unknown): void;
}

export function useNotePageUpdates(options: UseNotePageUpdatesOptions) {
  const optionsRef = React.useRef(options);
  React.useLayoutEffect(() => { optionsRef.current = options; });
  const controller = React.useMemo(() => createNotePageUpdates({
    request: request => optionsRef.current.request(request),
    onPage: (page, query) => optionsRef.current.onPage(page, query),
    onError: error => optionsRef.current.onError(error),
  }), [options.noteUpdates]);
  const key = noteQueryKey(options.query);

  React.useLayoutEffect(() => {
    controller.setQuery(optionsRef.current.query);
    controller.setEnabled(options.noteUpdates !== undefined && options.enabled !== false);
  }, [controller, key, options.noteUpdates, options.enabled]);

  React.useEffect(() => options.noteUpdates?.subscribe(event => controller.refresh(event.changedIds)), [controller, options.noteUpdates]);
  React.useEffect(() => () => controller.suspend(), [controller]);
  return controller;
}
